import { useMemo, useState, useEffect } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  CustomerServiceOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/authStore';
import {
  SupportTicketInput,
  useClientDashboard,
  useCreateSupportTicket,
  useSupportTickets,
} from '../hooks/useClientData';
import { supabase, functions } from '@/shared/lib/supabase';

const { Title, Paragraph, Text } = Typography;

const priorityColors: Record<string, string> = {
  low: 'default',
  medium: 'blue',
  high: 'red',
};

const priorityLabels: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
};

const categoryLabels: Record<string, string> = {
  technical: 'Técnico',
  financial: 'Financeiro',
  general: 'Geral',
  setup: 'Taxa de Setup',
};

const statusBadge: Record<string, 'default' | 'success' | 'processing' | 'warning' | 'error'> = {
  open: 'processing',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'default',
};

const statusLabels: Record<string, string> = {
  open: 'Aberto',
  in_progress: 'Em Progresso',
  resolved: 'Resolvido',
  closed: 'Fechado',
};

export default function ClientSupportPage() {
  const { user } = useAuthStore();
  const clientId = user?.id ?? null;
  const [searchParams] = useSearchParams();
  const [prefillHandled, setPrefillHandled] = useState(false);

  const { data: tickets, isLoading } = useSupportTickets(clientId);
  const { data: dashboardData } = useClientDashboard(clientId);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [lastMessageTime, setLastMessageTime] = useState<number>(0);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
  const [_setupPaymentInfo, setSetupPaymentInfo] = useState<any>(null);
  const [_isProcessingSetup, setIsProcessingSetup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('technical');

  const createTicket = useCreateSupportTicket();
  const [form] = Form.useForm<SupportTicketInput>();
  const queryClient = useQueryClient();

  // Constantes de validação
  const MESSAGE_MIN_LENGTH = 3;
  const MESSAGE_MAX_LENGTH = 2000;
  const COOLDOWN_SECONDS = 5;

  // Ler parâmetros da URL
  const workflowIdFromUrl = searchParams.get('workflow');
  const productIdFromUrl = searchParams.get('product');
  const descriptionFromUrl = searchParams.get('description');

  const productsOptions = useMemo(() => {
    return (
      dashboardData?.products.map((item) => ({
        value: item.id,
        label: item.product?.name ?? 'Workflow',
      })) ?? []
    );
  }, [dashboardData?.products]);

  // Fetch marketplace products for product selector
  const { data: marketplaceProducts = [] } = useQuery({
    queryKey: ['marketplace-products-support'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name')
        .eq('is_enabled', true)
        .eq('product_type', 'marketplace')
        .eq('marketplace_status', 'published')
        .order('name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const marketplaceProductOptions = useMemo(() => {
    return marketplaceProducts.map((product) => ({
      value: product.id,
      label: product.name,
    }));
  }, [marketplaceProducts]);

  useEffect(() => {
    if (prefillHandled) return;

    const shouldOpen = Boolean(workflowIdFromUrl) || Boolean(productIdFromUrl) || Boolean(descriptionFromUrl);
    if (!shouldOpen) return;

    if (workflowIdFromUrl && productsOptions.length === 0) return;
    if (productIdFromUrl && marketplaceProductOptions.length === 0) return;

    openModal();
    form.setFieldsValue({
      ...(workflowIdFromUrl ? { client_product_id: workflowIdFromUrl } : {}),
      ...(productIdFromUrl ? { marketplace_product_id: productIdFromUrl } : {}),
      ...(descriptionFromUrl ? { description: descriptionFromUrl } : {}),
    });
    setPrefillHandled(true);
  }, [workflowIdFromUrl, productIdFromUrl, descriptionFromUrl, productsOptions, marketplaceProductOptions, prefillHandled]);

  // Effect para atualizar o countdown do cooldown
  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const timer = setInterval(() => {
      setCooldownRemaining((prev) => {
        const newValue = prev - 1;
        return newValue <= 0 ? 0 : newValue;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  // Mutation para enviar resposta no ticket
  const sendReplyMutation = useMutation({
    mutationFn: async ({ ticketId, message }: { ticketId: string; message: string }) => {
      const { data, error } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: ticketId,
          user_id: clientId,
          message: message,
          is_internal: false,
          created_by_role: 'client',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidar a query das mensagens para recarregar
      queryClient.invalidateQueries({ queryKey: ['support-ticket-messages', selectedTicket?.id] });
      setReplyMessage('');
      // Definir cooldown
      const now = Date.now();
      setLastMessageTime(now);
      setCooldownRemaining(COOLDOWN_SECONDS);
    },
  });

  const handleSendReply = async () => {
    const trimmedMessage = replyMessage.trim();
    
    // Validações
    if (!trimmedMessage || !selectedTicket?.id) {
      return;
    }

    // Verificar tamanho mínimo
    if (trimmedMessage.length < MESSAGE_MIN_LENGTH) {
      message.warning(`A mensagem deve ter pelo menos ${MESSAGE_MIN_LENGTH} caracteres`);
      return;
    }

    // Verificar tamanho máximo
    if (trimmedMessage.length > MESSAGE_MAX_LENGTH) {
      message.warning(`A mensagem não pode ter mais de ${MESSAGE_MAX_LENGTH} caracteres`);
      return;
    }

    // Verificar cooldown
    const now = Date.now();
    const timeSinceLastMessage = (now - lastMessageTime) / 1000;
    
    if (timeSinceLastMessage < COOLDOWN_SECONDS) {
      const remainingSeconds = Math.ceil(COOLDOWN_SECONDS - timeSinceLastMessage);
      message.warning(`Aguarde ${remainingSeconds} segundo(s) antes de enviar outra mensagem`);
      return;
    }
    
    try {
      await sendReplyMutation.mutateAsync({
        ticketId: selectedTicket.id,
        message: trimmedMessage,
      });
    } catch (error) {
      console.error('Failed to send reply', error);
      message.error('Erro ao enviar mensagem. Tente novamente.');
    }
  };

  const { data: ticketMessages, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['support-ticket-messages', selectedTicket?.id],
    enabled: !!selectedTicket?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('id,message,created_by_role,created_at,is_internal')
        .eq('ticket_id', selectedTicket.id)
        .order('created_at', { ascending: true });

      if (error) {
        if (error.message.includes('does not exist')) {
          return [];
        }
        throw error;
      }

      // Filter out internal messages on client side
      // Show messages where is_internal is false, null, or undefined (anything except true)
      return (data ?? []).filter((msg) => msg.is_internal !== true);
    },
  });

  const openModal = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!clientId) return;

      const createdTicket = await createTicket.mutateAsync({ input: values, clientId });

      // If category is 'setup', create payment
      if (values.category === 'setup') {
        setIsProcessingSetup(true);
        try {
          const data = await functions.createSetupPayment({ ticket_id: createdTicket.id });

          if (data?.success) {
            setSetupPaymentInfo(data);
            message.success('Boleto de taxa de setup gerado com sucesso!');
            
            // Open the bank slip URL in a new tab
            if (data.bank_slip_url) {
              window.open(data.bank_slip_url, '_blank');
            }
          }
        } catch (error) {
          console.error('Error creating setup payment:', error);
          message.error('Erro ao gerar boleto de taxa de setup. Entre em contato com o suporte.');
        } finally {
          setIsProcessingSetup(false);
        }
      }

      setModalOpen(false);
      setSetupPaymentInfo(null);
    } catch (error) {
      console.error('Failed to create ticket', error);
    }
  };

  const columns = [
    {
      title: 'Chamado',
      dataIndex: 'title',
      key: 'title',
      render: (value: string, record: any) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value}</Text>
          <Text type="secondary">
            Aberto em {dayjs(record.created_at).format('DD/MM/YYYY HH:mm')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Categoria',
      dataIndex: 'category',
      key: 'category',
      render: (value: string) => <Tag icon={<AppstoreOutlined />}>{categoryLabels[value] || value}</Tag>,
    },
    {
      title: 'Prioridade',
      dataIndex: 'priority',
      key: 'priority',
      render: (value: string) => <Tag color={priorityColors[value]}>{priorityLabels[value] || value}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => <Badge status={statusBadge[value]} text={statusLabels[value] || value} />,
    },
    {
      title: 'Workflow',
      dataIndex: 'client_product_id',
      key: 'client_product_id',
      render: (value: string) =>
        dashboardData?.products.find((product) => product.id === value)?.product?.name || '-',
    },
    {
      title: 'Ações',
      key: 'actions',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <Button type="link" onClick={() => setSelectedTicket(record)}>
          Ver detalhes
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Row align="middle" justify="space-between">
        <Col>
          <Title level={3} style={{ marginBottom: 0, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Central de Suporte
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Acompanhe chamados abertos e abra novos tickets com a equipe FlowHub.
          </Paragraph>
        </Col>
        <Col>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={openModal} loading={createTicket.isPending}>
              Suporte
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              
              <div>
                <Title level={5} style={{ marginBottom: 4 }}>
                  Horário de atendimento
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  Segunda à Sexta, 9h às 18h - Horário de Brasília (GMT-3)
                </Paragraph>
              </div>
              <div>
                <Title level={5} style={{ marginBottom: 4 }}>
                  Canais
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  Chamados via portal e contato por e-mail flowhub.contato@gmail.com
                </Paragraph>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Card title="Histórico de chamados" loading={isLoading}>
            {tickets?.length ? (
              <Table
                columns={columns}
                dataSource={tickets?.map((ticket) => ({ ...ticket, key: ticket.id }))}
                pagination={{ pageSize: 6 }}
              />
            ) : (
              <Empty description="Nenhum chamado aberto" />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title="Abrir novo chamado"
        open={isModalOpen}
        okText="Enviar"
        cancelText="Cancelar"
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={createTicket.isPending}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Descreva o problema para que nossa equipe possa ajudar da forma mais rápida possível.
        </Paragraph>
        <Form layout="vertical" form={form}>
          <Form.Item
            name="title"
            label="Título do chamado"
            rules={[{ required: true, message: 'Informe um título' }]}
          >
            <Input placeholder="Ex: Erro ao executar workflow de onboarding" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Categoria"
            rules={[{ required: true, message: 'Selecione a categoria' }]}
            initialValue="technical"
          >
            <Select
              onChange={(value) => setSelectedCategory(value)}
              options={[
                { value: 'technical', label: 'Técnico' },
                { value: 'financial', label: 'Financeiro' },
                { value: 'general', label: 'Geral' },
                { value: 'setup', label: 'Taxa de Setup' },
              ]}
            />
          </Form.Item>

          {selectedCategory === 'setup' && (
            <Paragraph type="warning" style={{ marginBottom: 16 }}>
              A taxa de setup é opcional e pode ser contratada a qualquer momento. Ao criar este chamado, um boleto será gerado automaticamente.
            </Paragraph>
          )}

          <Form.Item 
            label="Workflow relacionado" 
            name="client_product_id"
            rules={[
              {
                required: selectedCategory === 'setup',
                message: 'Selecione um workflow ou produto para gerar a taxa de setup'
              }
            ]}
          >
            <Select 
              options={productsOptions} 
              allowClear 
              placeholder="Selecione um workflow" 
              onChange={(value) => {
                if (value) {
                  form.setFieldsValue({ marketplace_product_id: undefined });
                }
              }}
            />
          </Form.Item>

          <Form.Item 
            label="Produto relacionado" 
            name="marketplace_product_id"
            rules={[
              {
                validator: async (_, value) => {
                  if (selectedCategory === 'setup') {
                    const clientProductId = form.getFieldValue('client_product_id');
                    if (!clientProductId && !value) {
                      throw new Error('Selecione um workflow ou produto para gerar a taxa de setup');
                    }
                  }
                }
              }
            ]}
          >
            <Select 
              options={marketplaceProductOptions} 
              allowClear 
              placeholder="Selecione um produto do marketplace" 
              onChange={(value) => {
                if (value) {
                  form.setFieldsValue({ client_product_id: undefined });
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="priority"
            label="Prioridade"
            rules={[{ required: true, message: 'Selecione a prioridade' }]}
            initialValue="medium"
          >
            <Select
              options={[
                { value: 'low', label: 'Baixa' },
                { value: 'medium', label: 'Média' },
                { value: 'high', label: 'Alta' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descrição"
            rules={[{ required: true, message: 'Descreva o problema' }]}
          >
            <Input.TextArea rows={4} placeholder="Conte-nos o que está acontecendo" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        open={!!selectedTicket}
        width={480}
        onClose={() => {
          setSelectedTicket(null);
          setReplyMessage('');
          setCooldownRemaining(0);
        }}
        title={selectedTicket?.title}
      >
        {selectedTicket && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Status">
                <Badge status={statusBadge[selectedTicket.status]} text={statusLabels[selectedTicket.status] || selectedTicket.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Prioridade">
                <Tag color={priorityColors[selectedTicket.priority]}>{priorityLabels[selectedTicket.priority] || selectedTicket.priority}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Categoria">{categoryLabels[selectedTicket.category] || selectedTicket.category}</Descriptions.Item>
              <Descriptions.Item label="Aberto em">
                {dayjs(selectedTicket.created_at).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Workflow">
                {dashboardData?.products.find((product) => product.id === selectedTicket.client_product_id)?.product?.name ||
                  'Não informado'}
              </Descriptions.Item>
            </Descriptions>

            <Card title="Atualizações" loading={isLoadingMessages}>
              {ticketMessages?.length ? (
                <Timeline>
                  {ticketMessages.map((message) => (
                    <Timeline.Item
                      key={message.id}
                      dot={<CustomerServiceOutlined />}
                      color={message.created_by_role === 'client' ? 'blue' : 'green'}
                    >
                      <Text strong>{message.created_by_role === 'client' ? 'Você' : 'Equipe FlowHub'}</Text>
                      <Paragraph style={{ marginBottom: 0 }}>{message.message}</Paragraph>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        {dayjs(message.created_at).format('DD/MM/YYYY HH:mm')}
                      </Paragraph>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <Empty description="Nenhuma interação registrada" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>

            <Card title="Enviar mensagem">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Input.TextArea
                    rows={4}
                    placeholder="Digite sua mensagem..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    disabled={sendReplyMutation.isPending || cooldownRemaining > 0}
                    maxLength={MESSAGE_MAX_LENGTH}
                    showCount={{
                      formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
                    }}
                  />
                  {replyMessage.trim().length > 0 && replyMessage.trim().length < MESSAGE_MIN_LENGTH && (
                    <Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      Mínimo de {MESSAGE_MIN_LENGTH} caracteres
                    </Text>
                  )}
                </div>
                <Button
                  type="primary"
                  icon={<CustomerServiceOutlined />}
                  onClick={handleSendReply}
                  loading={sendReplyMutation.isPending}
                  disabled={
                    !replyMessage.trim() || 
                    replyMessage.trim().length < MESSAGE_MIN_LENGTH || 
                    cooldownRemaining > 0
                  }
                  block
                >
                  {cooldownRemaining > 0 
                    ? `Aguarde ${cooldownRemaining}s` 
                    : 'Enviar mensagem'}
                </Button>
              </Space>
            </Card>
          </Space>
        )}
      </Drawer>
    </div>
  );
}
